'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { useInView } from '@/hooks/useInView'

/* ── Lazy-loaded 3D globe (desktop only) ──────────────────── */
const Scene3D = dynamic(
  () => import('@/components/3d/Scene3D').then(m => ({ default: m.Scene3D })),
  { ssr: false }
)
const IntegrationGlobe = dynamic(
  () => import('@/components/3d/IntegrationGlobe').then(m => ({ default: m.IntegrationGlobe })),
  { ssr: false }
)

/* ── Partner data ────────────────────────────────────────── */
interface Partner {
  name: string
  description: string
  icon?: string  // path to /public icon, or undefined to use fallback
  fallbackLetter?: string
}

const PARTNERS: Partner[] = [
  { name: 'QuickBooks',        description: 'Accounting & invoicing sync',      icon: '/assets/icons/quickbooks.png' },
  { name: 'Google Workspace',  description: 'Email, calendar & drive',           icon: '/assets/icons/google.png' },
  { name: 'EagleView',         description: 'Satellite roof measurements',       icon: '/assets/icons/eagleview.png' },
  { name: 'Twilio',            description: 'SMS & voice communication',         icon: '/assets/icons/twilio.png' },
  { name: 'Stripe',            description: 'Payment processing',                fallbackLetter: 'S' },
  { name: 'ABC Supply',        description: 'Material ordering',                 icon: '/assets/icons/abc-supply.png' },
  { name: 'SRS Distribution',  description: 'Material ordering',                 icon: '/assets/icons/srs-distribution.png' },
  { name: 'QXO',               description: 'Roofing supply & pricing',          icon: '/assets/icons/qxo.png' },
  { name: 'Sierra AI',         description: 'AI-powered automation',             icon: '/assets/icons/gemini.png' },
]

/* ── Partner icon ─────────────────────────────────────────── */
function PartnerIcon({ partner }: { partner: Partner }) {
  if (partner.icon) {
    return (
      <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center p-2 group-hover:border-zinc-700 transition-colors">
        <Image
          src={partner.icon}
          alt={partner.name}
          width={40}
          height={40}
          className="w-full h-full object-contain"
        />
      </div>
    )
  }
  return (
    <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:border-zinc-700 transition-colors">
      <span className="text-lg font-bold text-red-500">
        {partner.fallbackLetter ?? partner.name.charAt(0)}
      </span>
    </div>
  )
}

/* ── CSS Orbital Globe (replaces 3D globe) ───────────────── */
function OrbitGlobe({ isInView }: { isInView: boolean }) {
  const count = PARTNERS.length
  const radius = 180 // px — radius of the orbit circle

  return (
    <div
      className={`anim-scale-in ${isInView ? 'in-view' : ''}`}
      style={{ transitionDelay: '0.3s', transitionDuration: '0.8s' }}
    >
      <div className="relative mx-auto" style={{ width: radius * 2 + 80, height: radius * 2 + 80 }}>
        {/* Center glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-red-600/10 blur-2xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border border-red-600/20 flex items-center justify-center">
          <span className="text-xs font-semibold text-red-500 tracking-wide">BL</span>
        </div>

        {/* Orbit ring (visual guide) */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-zinc-800/40"
          style={{ width: radius * 2, height: radius * 2 }}
        />

        {/* Orbiting container — rotates the entire group */}
        <div
          className="orbit-container absolute top-1/2 left-1/2"
          style={{ width: 0, height: 0 }}
        >
          {PARTNERS.map((partner, i) => {
            const angle = (360 / count) * i
            return (
              <div
                key={partner.name}
                className="orbit-item absolute"
                style={{
                  transform: `rotate(${angle}deg) translateX(${radius}px) rotate(-${angle}deg)`,
                  /* Position the icon centered at its orbit point */
                  left: -24,
                  top: -24,
                }}
              >
                <div className="group cursor-default" title={`${partner.name}: ${partner.description}`}>
                  <PartnerIcon partner={partner} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ── Mobile partner grid ─────────────────────────────────── */
function MobileGrid({ isInView }: { isInView: boolean }) {
  return (
    <div
      className={`anim-fade-in-up ${isInView ? 'in-view' : ''} grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4`}
      style={{ transitionDelay: '0.2s', transitionDuration: '0.7s' }}
    >
      {PARTNERS.map((partner, i) => (
        <div
          key={partner.name}
          className={`anim-fade-in-up anim-delay-${Math.min(i + 1, 8)} ${isInView ? 'in-view' : ''} glass-card p-4 sm:p-5 flex flex-col items-center text-center gap-2.5 group cursor-default hover:border-red-600/30 hover:scale-[1.04] hover:-translate-y-0.5 transition-all duration-300`}
        >
          <PartnerIcon partner={partner} />
          <div>
            <p className="text-sm font-semibold text-white">{partner.name}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5 leading-snug">
              {partner.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

/* ── Main component ──────────────────────────────────────── */
export function IntegrationsOrbit() {
  const { ref, isInView } = useInView()
  const [isDesktop, setIsDesktop] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
    const mql = window.matchMedia('(min-width: 1024px)')
    setIsDesktop(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return (
    <section className="section noise-overlay overflow-hidden">
      <div className="section-inner" ref={ref}>
        {/* Section Header */}
        <div
          className={`anim-fade-in-up ${isInView ? 'in-view' : ''} text-center mb-8 sm:mb-10`}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-4">
            Integrations
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-3xl mx-auto">
            Connects With{' '}
            <span className="gradient-text-red">Your Stack.</span>
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 mt-3 max-w-xl mx-auto">
            Seamlessly integrates with the tools you already use.
          </p>
        </div>

        {/* Content: CSS orbit on desktop, icon grid on mobile */}
        {hasMounted && (
          <>
            {isDesktop ? (
              <div
                className={`anim-scale-in ${isInView ? 'in-view' : ''} mx-auto`}
                style={{ transitionDelay: '0.3s', transitionDuration: '0.8s', height: 480, maxWidth: 600 }}
              >
                <Scene3D
                  className="w-full h-full"
                  camera={{ position: [0, 0, 6], fov: 50 }}
                  autoRotate
                  fallback={<OrbitGlobe isInView={isInView} />}
                >
                  <IntegrationGlobe radius={2} rotateSpeed={0.003} />
                </Scene3D>
              </div>
            ) : (
              <MobileGrid isInView={isInView} />
            )}
          </>
        )}

        {/* Bottom text */}
        <p
          className={`anim-fade-in ${isInView ? 'in-view' : ''} text-center text-sm text-zinc-500 mt-8`}
          style={{ transitionDelay: '1.0s' }}
        >
          Automations module coming in V2 · MCP available on request
        </p>
      </div>
    </section>
  )
}
